import { useEffect, useState } from 'react';
import { webKeyboardInset } from '../lib/web-keyboard-inset';

/**
 * Height (px) the on-screen keyboard is currently covering at the bottom of the
 * layout viewport, on mobile web.
 *
 * iOS Safari (and iOS in-app WebViews) shrink the *visual* viewport when the
 * virtual keyboard opens but leave the *layout* viewport untouched, so a
 * `position: sticky; bottom: 0` composer ends up pinned to the layout-viewport
 * bottom: behind the keyboard. There is no CSS-only fix on iOS
 * (`interactive-widget` is Chrome-only), so we measure the overlap via the
 * VisualViewport API and let the caller subtract it from the chat height.
 *
 * Returns 0 when there is no keyboard, on browsers without VisualViewport, and
 * during SSR.
 */

// URL-bar show/hide and rubber-band scrolling nudge the visual viewport by a
// few dozen px; only treat a sizable overlap as a keyboard so those don't
// resize the layout.
export function useWebKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // offsetTop covers the case where Safari has scrolled the layout viewport
      // to reveal a focused field; the remaining gap below the visual viewport
      // is what the keyboard occupies.
      const active = document.activeElement;
      const editing = active instanceof HTMLElement &&
        (active.matches('input, textarea') || active.isContentEditable);
      setInset(webKeyboardInset({ width: window.innerWidth, layoutHeight: window.innerHeight,
        visualHeight: vv.height, offsetTop: vv.offsetTop, scale: vv.scale, editing }));
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('pageshow', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('pageshow', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);

  return inset;
}
